/**
 * Vertical-aware vocabulary — the one mechanism that replaces five proxies.
 *
 * Per `docs/design/vertical-aware-case-model.md` (platform repo), the front
 * end was making vertical-dependent decisions in five places, each using a
 * different stand-in: a URL path prefix, `workflow_keys.length > 0`, a
 * session's definition plan, an implicit "only IDW reaches this map", and —
 * in the voucher flow — nothing at all. One real field collapses all five.
 *
 * That field is `organizations.industry`, and it reaches the front end by two
 * routes, because the two kinds of surface have two different things to ask:
 *
 *   - **Operator surfaces** — `GET /operator/me` → `org.industry`.
 *   - **Public journeys** — `GET /sessions/:token/status` → `org_industry`.
 *     `voucherApi.activate` returns a token that resolves against that same
 *     route, so one field serves the applicant flow *and* the voucher flow.
 *
 * Both are read through `verticalFrom()` below. Nothing else should touch the
 * raw string.
 *
 * ## Keyed off the vertical, never off org name or subdomain
 *
 * This is the rule `relationshipOptions.ts` set for this codebase and it is
 * worth keeping: no org is named in front-end code, so a definition-driven
 * flow reads correctly for any org that gets one. `operatorHome.ts` used
 * `workflow_keys` for the same reason — the best proxy available before this
 * field shipped. It is no longer the best one.
 *
 * ## This is tier 3 of three, and tier 3 lives in code
 *
 * The design record separates three tiers of label, and conflating them is
 * how this module would sprawl:
 *
 *   - **Identity** (`workflow_key`, `step_type`, `signal_type`) — frozen
 *     persisted strings. Nobody changes these, ever (ADR-013 §D1).
 *   - **Presentation** (`display_name` on a flow) — varies per flow instance,
 *     lives in versioned data, changed by DigaVouch personnel.
 *   - **Vocabulary** (this file) — varies by *vertical*, a product decision,
 *     in code.
 *
 * Tier 3 deliberately has **no data home**, which is correct while there are
 * two DigaVouch-authored verticals. If a customer ever needs to override a
 * word, the escape hatch is C.7's org override affordance — not a per-org copy
 * table bolted onto this one.
 */

/**
 * The verticals the product speaks today.
 *
 * These are the values `organizations.industry` actually holds since the
 * platform's 2026-08-28 rename (`multifamily` → `residential_leasing`,
 * `recruiting` → `hr_recruiting`). ADR-014 D8 chose the specific names over
 * the generic ones because other kinds of leasing and recruiting are expected.
 */
import { APPLICANT_STEP } from "@/lib/flowPlan";

export type KnownVertical = "residential_leasing" | "hr_recruiting";

/**
 * An org's vertical as this front end reads it: one it knows, or `"unknown"`.
 *
 * `"unknown"` is its own vertical, not leasing (Juan, 2026-10-14). It covers
 * an absent, empty, legacy or unrecognised `industry`, and an operator whose
 * membership has not loaded yet. Its COPY is leasing's (`copyForVertical`),
 * because that is what every such surface rendered before this value existed.
 * Its RATING is nothing: only `residential_leasing` rates the applicant, so a
 * vertical this front end cannot name fails closed, the same rule the API
 * applies when it decides who is sent a rating.
 */
export type Vertical = KnownVertical | "unknown";

export const UNKNOWN_VERTICAL = "unknown" satisfies Vertical;

/**
 * Whose copy an unknown vertical, and a caller that passes no vertical, reads.
 *
 * Leasing, because it is the incumbent: every pre-rename org, every org on a
 * server too old to send the field, and every surface with no org context at
 * all was already rendering leasing copy. Falling back to it changes nothing
 * for anyone, whereas defaulting to recruiting would silently re-word live
 * Rockwell surfaces the moment a request failed.
 *
 * ⚠️ Copy only. The rating predicates below do not fall back to it.
 */
export const DEFAULT_VERTICAL: KnownVertical = "residential_leasing";

/**
 * The values `organizations.industry` may hold: the same list the platform's
 * create endpoint validates against (verified as of 2026-10-14). Adding a
 * vertical there means adding it here, with its copy and its label.
 */
export const KNOWN_VERTICALS: readonly KnownVertical[] = [
  "residential_leasing",
  "hr_recruiting",
];

/**
 * The name an admin reads for each vertical when creating an organization.
 * Internal (the admin `/tenants` page), never shown to an applicant.
 */
export const INDUSTRY_LABELS: Record<KnownVertical, string> = {
  residential_leasing: "Residential leasing",
  hr_recruiting: "Recruiting",
};

/**
 * Narrow the wire's open-ended string to a vertical this front end can render.
 *
 * ⚠️ **Unrecognised must fall back, never throw.** The backend deliberately
 * stores `industry` with no CHECK constraint so that a new kind of
 * organization costs no migration, and `dv-api-contract` mirrors that with
 * `z.string()` rather than an enum — its own comment says a value the consumer
 * does not recognize "should fall back, not fail". A third vertical appearing
 * server-side must therefore degrade on an un-updated front end, not
 * white-screen it.
 *
 * It degrades to `"unknown"`: leasing copy, no rating. `undefined` (not
 * loaded yet) lands there too, so a caller that cannot wait still shows no
 * rating; one that can should still hold on `useOperatorVertical().isResolved`
 * rather than paint the unknown page and then re-word it.
 */
export function verticalFrom(industry: string | null | undefined): Vertical {
  return KNOWN_VERTICALS.includes(industry as KnownVertical)
    ? (industry as KnownVertical)
    : UNKNOWN_VERTICAL;
}

/**
 * Sugar for the common branch. Deliberately asks about the *vertical* and not
 * about a flow: `ApplicantFlow`'s existing `isRecruiting = definitionPlan
 * !== null` answers "does this session run a workflow definition?", which is a
 * different question that happens to have had the same answer so far.
 */
export function isRecruiting(vertical: Vertical): boolean {
  return vertical === "hr_recruiting";
}

/**
 * The words that differ between verticals.
 *
 * Every field here is tier-3 vocabulary. Add a field when a second surface
 * needs the same word, not to pre-empt one — a copy key with one call site is
 * just an indirection.
 *
 * ⚠️ **Not on this path: the compliance disclaimers.** The "DigaVouch does not
 * approve or deny rental applications… final application decisions remain with
 * the property team" strings in `caseStatus.ts`, `Applicant.tsx` and
 * `VoucherFlow.tsx` are written against a tenant-screening posture, and the
 * employment equivalent sits under a different regime. The company
 * constitution commits to real legal advice before entering a vertical. Those
 * strings need counsel, not a lookup table, and must not be added here as a
 * mechanical swap — it is the one place in this change where the mechanical
 * fix is the wrong one.
 */
export interface VerticalCopy {
  /**
   * The Layer-1 locator's operator-visible label — the dashboard column, the
   * Send-Request field, and the detail page's line.
   *
   * Backed by `inquiries.applied_for`, a vertical-neutral column under two
   * vertical-specific labels. In leasing it names a unit; in recruiting it
   * names the job applied for.
   *
   * 🚪¹ **"Role" is decision 2 and is not confirmed** — external vocabulary
   * under ADR-001, still open with Patrick. Deliberately the value in the
   * code rather than a placeholder, so the surfaces are reviewable now; a
   * different answer is a one-line edit here and nowhere else.
   *
   * ⚠️ Note the collision decision 2 resolves: ADR-014 spends "Role Type" on
   * the *flow archetype* (Early Careers, Tech Role…). A column headed "Role"
   * showing a job title, beside a control headed "Role Type" offering "Tech
   * Role", is two near-identical names for different things — which is why
   * the operator sees one control, not two.
   */
  locatorLabel: string;

  /** Placeholder for the locator input in the Send-Request dialog. */
  locatorPlaceholder: string;

  /**
   * What the verified person is called, in sentence position.
   *
   * 🚪¹ **Decision 8, also open with Patrick.** `applicant` stays the
   * *internal* register — the route is `/applicant/:id` and does not change;
   * this is only the visible word. That is ADR-001's two-register rule applied
   * a second time, the same way `case` and `inquiry` already split.
   *
   * Rejected for the recruiting word: *subject* (clinical, against soul.md),
   * *consumer* (FCRA's own term — adopting it arguably asserts a CRA posture),
   * *person* (PortFi's noun, too generic for a screen), *individual* / *party*
   * (bureaucratic).
   */
  applicantNoun: string;
  applicantNounPlural: string;

  /**
   * ⚠️ Both nouns are stored **lowercase, for mid-sentence use**. Surfaces
   * that need them at a sentence start or as a column header run them through
   * `toTitleCase`, rather than this table carrying a second cased copy of
   * every noun that would then have to be kept in sync with the first.
   */


  /**
   * The queue's search placeholder. Not composed from the fields above,
   * because the two verticals search different *sets* of things rather than
   * the same set under different names: recruiting has no follow-up reason to
   * search (see `SHOWS_FOLLOW_UP_REASON` below), so its placeholder has two
   * terms where leasing has three.
   */
  searchPlaceholder: string;

  /**
   * The Send-Request dialog's eyebrow.
   *
   * "Exception Workflow" is a precise description of what DigaVouch is in
   * leasing — an exception path an operator invokes for one applicant who
   * needs extra scrutiny. It is simply false in recruiting, where the lane
   * applies to everyone who applies to the role. Same reason the follow-up
   * reason has no recruiting counterpart.
   */
  sendRequestEyebrow: string;

  /**
   * Who is on the other side of the verification, as the applicant and the
   * voucher are told about them.
   *
   * ⚠️ **Stored WITHOUT its article and in sentence case** — `"property team"`,
   * not `"The property team"`. Call sites supply `The ` or `the ` themselves,
   * because the same words appear at a sentence start (*"The property team
   * asks applicants to…"*) and mid-sentence (*"…share the details with the
   * property team"*). `toTitleCase` is the wrong tool here: it would produce
   * *"The Property Team"*, which is not what any of these sentences say.
   *
   * Used only where the org's own name is unavailable — every one of these
   * surfaces prefers `session.org_name` and falls back to this.
   */
  reviewingTeam: string;

  /**
   * The decision DigaVouch explicitly does **not** make, as a bare adjective:
   * *"DigaVouch does not make the {rental|hiring} decision."*
   *
   * This one line is a *scope* statement, not a compliance disclaimer — it
   * says what DigaVouch is, and it is true in both verticals. The disclaimers
   * proper (liability, FCRA-shaped posture) are deliberately absent from this
   * module; see the note above.
   */
  decisionKind: string;

  /** Example text in the "tell us why" box on the decline screen. */
  declineReasonPlaceholder: string;

  /**
   * The voucher flow's opening headline — the first thing a referee reads.
   *
   * Not composed from anything above: the two verticals name different good
   * outcomes, and the sentence is the one place the voucher is told what they
   * are helping with at all.
   */
  voucherHeroHeadline: string;

  /**
   * Page 1 of the applicant flow, under the "Trust Checkpoint" framing
   * (Patrick, 2026-09-01).
   *
   * `null` on a vertical that has no such concept — `ApplicantFlow` then
   * renders exactly the copy it rendered before, so leasing is untouched by
   * this block existing.
   *
   * `{org}` is replaced with the organisation's name at render.
   *
   * ⚠️ The optionality notice is the ONLY place the flow may call anything
   * optional. Patrick's rule: the choice is whether to do the Trust Checkpoint
   * at all, not whether to do a step inside it — so an optional STEP must not
   * be labelled optional once the applicant is in.
   */
  checkpointIntro: {
    /** Page-level H1 and H2. */
    headline: string;
    subhead: string;
    /** The step-0 card's own heading and line, above the step list. */
    cardTitle: string;
    cardSubhead: string;
    /**
     * Page 1's step-list wording, keyed by `APPLICANT_STEP` index.
     *
     * Deliberately NOT `STEP_TYPE_PRESENTATION`. That map drives the heading
     * on each step's own card and is pinned vertical-neutral by
     * `flowPlanVocabulary.test.ts` — whose docblock says the day a step needs
     * different words per vertical, the vertical gets threaded in rather than
     * the map edited. This copy appears on page 1 only.
     *
     * A step with no entry here falls back to its plan entry.
     */
    stepList: Record<number, { title: string; body: string }>;
    /**
     * The footer line on every step AFTER consent.
     *
     * This is where the optionality lives in Patrick's mockups — one quiet
     * line carried through the flow, rather than a block on page 1. It is the
     * only place the flow may call anything optional, and it names the
     * Checkpoint, never a step inside it.
     */
    footerNote: string;
    /**
     * Page-level H1 + H2 per step, keyed by `APPLICANT_STEP` index, from
     * Patrick's v6 mockups — each screen leads with its own sentence rather
     * than one generic line repeated down the flow.
     *
     * A step with no entry falls back to the generic header.
     */
    pageHeaders: Record<number, { h1: string; h2: string }>;
    /** The purple reassurance panel on the ID screen. */
    idvNotice: string[];
    /**
     * The consent screen's primary button.
     *
     * ⚠️ The one place the written note beats the v6 mockups (Juan, 2026-09-02).
     * The mockups show "Accept & Begin" unchanged, but page 1 is the roughest
     * of the six — it also carries a stray second Continue — and the note is
     * explicit about this string. Everything else on page 1 follows the
     * mockups; the SECONDARY action and the consent line deliberately do NOT
     * take the note's versions.
     */
    primaryCta: string;
    /** The purple notice above Submit on the review step. */
    submitNotice: { title: string; body: string };
    /**
     * The exit confirmation.
     *
     * Reached from the "Trust Checkpoint is optional" line on every screen.
     * The wording it replaced — "this verification request will end and no
     * identity signals will be shared. You can't undo this from here." —
     * described the loss and nothing else. The application does not end; it
     * carries on without the extra context, and that is what the applicant
     * needs to know before choosing.
     */
    exit: { title: string; body: string; confirmCta: string; cancelCta: string };
    /** The post-submit screen. */
    complete: { h1: string; h2: string; cardTitle: string; cardBody: string; cardFootnote: string };
  } | null;
}

export const VERTICAL_COPY: Record<KnownVertical, VerticalCopy> = {
  residential_leasing: {
    locatorLabel: "Unit / Property",
    locatorPlaceholder: "e.g. Unit 4B, Riverside Apartments",
    applicantNoun: "applicant",
    applicantNounPlural: "applicants",
    searchPlaceholder: "Search applicants, units, or reasons",
    sendRequestEyebrow: "Exception Workflow",
    reviewingTeam: "property team",
    decisionKind: "rental",
    declineReasonPlaceholder:
      "e.g. I rented elsewhere, or I'd prefer not to share these details",
    voucherHeroHeadline: "Help them secure their new apartment lease.",
    // No Trust Checkpoint framing in leasing — page 1 keeps its existing copy.
    checkpointIntro: null,
  },
  hr_recruiting: {
    locatorLabel: "Role",
    locatorPlaceholder: "e.g. Senior Backend Engineer",
    applicantNoun: "applicant",
    applicantNounPlural: "applicants",
    searchPlaceholder: "Search applicants or roles",
    sendRequestEyebrow: "Verification Request",
    reviewingTeam: "hiring team",
    decisionKind: "hiring",
    declineReasonPlaceholder:
      "e.g. I'm no longer pursuing this role, or I'd prefer not to share these details",
    voucherHeroHeadline: "Help them move forward with their job application.",
    checkpointIntro: {
      headline: "Put a little more you behind your application.",
      subhead:
        "Applications can look similar on paper. A few quick checks give the hiring team more context about the person behind yours.",
      cardTitle: "A little more than an application.",
      cardSubhead:
        "Complete your Trust Checkpoint and we'll let the recruiter know when you're done.",
      stepList: {
        [APPLICANT_STEP.EMAIL]: {
          title: "Verify your email",
          body: "Confirm the email address on your application.",
        },
        [APPLICANT_STEP.IDV]: {
          title: "Confirm your identity",
          body: "A quick identity check, if this Checkpoint includes one.",
        },
        [APPLICANT_STEP.ACCOUNTS]: {
          title: "Connect a digital account",
          body: "Choose an account you already use.",
        },
        [APPLICANT_STEP.VOUCH]: {
          // Matches `TRAILING_ENTRIES` in `flowPlan.ts`, which is what the
          // step's own card renders ~90 seconds later. Was "Ask someone who
          // knows you" — same step, two verbs, no way for the applicant to
          // tell they're the same thing (va#177, A2 finding 2026-09-18).
          title: "Invite someone who knows you",
          body: "A quick confirmation from someone who knows you.",
        },
        [APPLICANT_STEP.REVIEW]: {
          title: "Review and submit",
          body: "Check what you're sharing before you submit.",
        },
      },
      footerNote: "Trust Checkpoint is optional · Exit to standard review",
      pageHeaders: {
        [APPLICANT_STEP.IDV]: {
          h1: "Confirm it's you",
          h2: "A quick identity check helps confirm the person behind the application",
        },
        [APPLICANT_STEP.EMAIL]: {
          h1: "Start with something simple.",
          h2: "Confirm the email you applied with.",
        },
        [APPLICANT_STEP.ACCOUNTS]: {
          h1: "Add a little more context.",
          h2: "Connect one account you already use. One is enough.",
        },
        [APPLICANT_STEP.VOUCH]: {
          h1: "Bring in someone who knows you.",
          h2: "A quick confirmation from someone who knows you adds a human signal beyond the application.",
        },
        [APPLICANT_STEP.REVIEW]: {
          h1: "That's everything.",
          h2: "Submit your Trust Checkpoint and we'll let the recruiter know you completed it.",
        },
      },
      idvNotice: [
        "We'll show you what information is being requested before you continue.",
        "We don't use your ID to make hiring decisions.",
      ],
      primaryCta: "Start Trust Checkpoint",
      submitNotice: {
        title: "When you submit, we'll notify the recruiter.",
        body: "Your application continues in the standard hiring process.",
      },
      exit: {
        title: "Continue without Trust Checkpoint?",
        body:
          "Your application will continue through the standard review process. Your Trust Checkpoint will not be completed.",
        confirmCta: "Continue standard review",
        cancelCta: "Stay in Trust Checkpoint",
      },
      complete: {
        h1: "Your update is with the hiring team.",
        h2: "We've notified the recruiter that you completed your Trust Checkpoint.",
        cardTitle: "Your update is with the hiring team.",
        cardBody:
          "They now have your application plus the additional context you chose to provide.",
        cardFootnote:
          "Your application remains part of the employer's standard hiring process.",
      },
    },
  },
};

/** The copy set for a vertical. `"unknown"` reads `DEFAULT_VERTICAL`'s. */
export function copyForVertical(vertical: Vertical): VerticalCopy {
  return VERTICAL_COPY[vertical === UNKNOWN_VERTICAL ? DEFAULT_VERTICAL : vertical];
}

/** Convenience for callers holding a raw wire value rather than a `Vertical`. */
export function copyForIndustry(
  industry: string | null | undefined,
): VerticalCopy {
  return copyForVertical(verticalFrom(industry));
}

/**
 * Does this vertical have a follow-up reason at all?
 *
 * **This is structure, not vocabulary, which is why it is a predicate and not
 * a copy field.** In leasing the reason records *why this one applicant got
 * extra scrutiny* — DigaVouch is an exception path there. In recruiting the
 * lane applies to everyone who applies to the role, so there is no
 * per-candidate reason to record. It is not a word that needs translating; it
 * is a concept that does not exist on that side.
 *
 * ⚠️ **Do not invent recruiting reasons to fill the gap.** `follow_up_reason`
 * stays nullable and leasing-only (the column accepts NULL and the endpoint
 * never rejects a missing reason), and the recruiting counterpart of the
 * reason is the *flow* — which needs `display_name` on an API before it can
 * be picked. Until then recruiting simply has one field fewer.
 *
 * Governs three surfaces together, and they must not drift apart: the
 * Send-Request dialog's reason select, the queue's Verification Reasons
 * panel, and whether the search placeholder mentions reasons.
 */
export function showsFollowUpReason(vertical: Vertical): boolean {
  return !isRecruiting(vertical);
}

/**
 * Does the Send-Request dialog collect a date of birth?
 *
 * Leasing only. It is optional today and harmless there, but on the hiring
 * side it is **age data captured by a recruiter at screening time**, which is
 * a different thing entirely — company constitution §6 (compliance-aware from
 * day one) is the basis, and employment screening sits under EEOC-shaped
 * rules that tenant screening does not.
 *
 * The applicant still supplies a DOB to IDV, where it is actually needed and
 * where the recruiter never sees it. Nothing is lost from the verification;
 * what changes is who holds it.
 */
export function collectsDateOfBirth(vertical: Vertical): boolean {
  return !isRecruiting(vertical);
}

/**
 * Does this vertical rate the applicant to the operator?
 *
 * **Structure, not vocabulary, which is why it is a predicate and not a copy
 * field** — same shape as `showsFollowUpReason` above. It is not that
 * recruiting words the rating differently; it is that recruiting does not have
 * one. There is no recruiting translation of "High Confidence" to put in a
 * lookup table, and inventing one is the failure mode this predicate exists to
 * prevent.
 *
 * The 2026-09-17 founder sync, Patrick in writing the same day:
 *
 * > Scores stay internal and logged. Recruiters shouldn't see a score or use
 * > it as a recommendation. They should see the Trust Checkpoint state, the
 * > actual evidence, and any factual review notes. [...] role determines
 * > required assurance -> applicant provides evidence -> recruiter sees
 * > evidence/state -> human decides.
 *
 * Governs three surfaces on the operator detail page, and they must not drift
 * apart: the confidence headline and its summary sentence, the
 * AI-assisted-recommendation pill, and the "DigaVouch result - Verified / Not
 * Verified" label on a result event.
 *
 * ## What this deliberately does NOT govern
 *
 * The **status pill** ("Needs Review" and the two review outcomes). That is
 * the case *state*, which is the one thing Patrick's note says a recruiter
 * SHOULD see. It is worded in `caseStatus.ts`, not here: in recruiting the
 * two outcomes read "Reviewed, no findings" and "Reviewed, findings noted",
 * because "Verified" and "Not Verified" read as a verdict on the person.
 * All of those recruiting words sit in one block in `caseStatus.ts`, so a
 * later rename is one edit there.
 *
 * ## Unknown has no rating: it fails closed (Juan, 2026-10-14)
 *
 * Only an org that is explicitly `residential_leasing` rates the applicant.
 * `"unknown"` (absent, legacy, unrecognised, or not loaded yet) reads as
 * recruiting does here: no headline, no pill, a neutral Timeline label. Until
 * 2026-10-14 it read as leasing, so an org with a stray `industry` value got
 * the rating's wording around evidence the API no longer sends it. The API
 * makes the same choice (verified as of 2026-10-14, platform PR #809, merged:
 * the rating goes only to `residential_leasing`). Callers that can wait should
 * still gate on
 * `useOperatorVertical().isResolved` rather than paint the page and re-word it.
 */
export function showsApplicantRating(vertical: Vertical): boolean {
  return vertical === "residential_leasing";
}

/**
 * May the page show the rating right now? `showsApplicantRating`, held until
 * `useOperatorVertical().isResolved`. Every rating surface on a page reads
 * this one answer (the evidence card's title, its headline and pill, the
 * Timeline's result label), so none of them can forget the hold.
 */
export function canShowApplicantRating(vertical: Vertical, isResolved: boolean): boolean {
  return isResolved && showsApplicantRating(vertical);
}

/**
 * Does the operator detail page show the rental scope note ("Important note:
 * DigaVouch does not approve or deny rental applications...")?
 *
 * A predicate, not a copy field: the note's text stays in `caseStatus.ts` and
 * stays out of this deck (see "Not on this path" on `VerticalCopy`). That
 * text is written for tenant screening; the employment equivalent needs
 * counsel, so a recruiting case shows no note rather than the rental one or
 * an improvised one. When counsel's wording exists, this becomes "both
 * verticals, each with its own note".
 *
 * `"unknown"` keeps the note: it is copy, not a rating, and an unknown
 * vertical reads leasing's copy. Callers hold it until
 * `useOperatorVertical().isResolved`, since an unresolved vertical reads as
 * unknown and would otherwise flash it for a recruiter.
 */
export function showsRentalScopeNote(vertical: Vertical): boolean {
  return !isRecruiting(vertical);
}

/**
 * Does the operator detail page render the "AI Review Summary" card at all?
 *
 * platform#600 part 1. The 2026-09-17 founder sync said a recruiter sees the
 * Trust Checkpoint state, the evidence, and factual review notes, never a
 * score or anything that reads as a recommendation. `showsApplicantRating`
 * above answered that for the confidence headline and the pill next to it.
 * This card is a different problem: it holds a model-written `narrative` plus
 * a section headed "Suggested read" addressed to the person making the hiring
 * decision, and that narrative is generated by a prompt whose few-shot
 * examples restate the confidence band in prose, so the rating the founders
 * removed comes back as a sentence one card higher, ungated.
 *
 * ## A SIBLING predicate, not a reuse of `showsApplicantRating`
 *
 * The two happen to agree today (both are `!isRecruiting(vertical)`), and
 * that similarity is exactly why they must stay two functions. platform#600
 * stays open for part 2: what a recruiter gets *instead* of this card is an
 * undecided product question with at least three coherent answers (nothing,
 * a factual summary with no judgement, or a recruiting-specific prompt). That
 * decision is about this predicate alone and has no bearing on whether
 * recruiting ever gets a rating: the two can diverge, and a shared predicate
 * would not let them. If part 2 lands as "yes, a factual summary" this
 * function's body changes; `showsApplicantRating` does not move.
 *
 * ## Unknown has no card: it fails closed, like `showsApplicantRating`
 *
 * The narrative restates the rating in prose, so it follows the rating, not
 * the copy: only `residential_leasing` gets the card. The API already withheld
 * the summary from an unrecognised industry (verified as of 2026-10-14,
 * platform PR #809's description), so an unknown org saw
 * an empty card reading "AI summary not generated yet." and now sees none.
 */
export function showsAiReviewSummary(vertical: Vertical): boolean {
  return vertical === "residential_leasing";
}

/**
 * The detail page's "back to the queue" link.
 *
 * A function rather than a `VerticalCopy` field because the two sides answer
 * it differently in kind, not just in wording. The leasing queue is a page
 * with a **name** — "Verification Center" is its `<h1>` on `/dashboard`, and
 * `e2e/specs/j3-operator-dashboard.spec.ts` asserts that heading — so its link
 * names the destination. The recruiting dashboard's hero has no page title to
 * name, so its link names what is there instead, composed from the noun.
 *
 * Composed deliberately: decision 8 (Applicant vs Candidate, still 🚪¹ open
 * with Patrick) stays a **one-line edit** to `applicantNounPlural`. A second
 * literal holding the same word is exactly the drift #167 removed.
 */
export function queueBackLabel(vertical: Vertical): string {
  return isRecruiting(vertical)
    ? `Back to all ${copyForVertical(vertical).applicantNounPlural}`
    : "Back to Verification Center";
}
